import { useEffect } from 'react'
import { BlogLayout } from '../../components/BlogLayout'
import { setPageMeta } from '../../utils'

export function AiSafetyGuidePost() {
  useEffect(() => {
    setPageMeta({
      title: 'How to Use AI Safely with Confidential Documents | arisk.ai',
      description: 'A practical guide for legal, finance, and healthcare professionals who need to use AI tools without exposing sensitive data.',
      canonical: 'https://arisk.ai/blog/how-to-use-ai-safely-with-confidential-documents',
    })
  }, [])

  return (
    <BlogLayout
      title="How to Use AI Safely with Confidential Documents"
      description="A practical guide for legal, finance, and healthcare professionals who need to use AI tools without exposing sensitive data."
      date="2026-01-18"
      readTime="6 min read"
      url="https://arisk.ai/blog/how-to-use-ai-safely-with-confidential-documents"
      author="Alex Rowe"
      relatedPosts={[
        { slug: 'does-chatgpt-store-your-conversations', title: 'Does ChatGPT Store Your Conversations?' },
        { slug: 'what-happens-to-data-you-paste-into-claude', title: 'What Happens to Data You Paste into Claude?' },
      ]}
    >
      <p>
        AI tools have become genuinely useful for working with documents: summarising long contracts,
        extracting key terms, drafting responses, spotting inconsistencies. For professionals in law,
        finance, healthcare, and compliance, the productivity case is clear. The problem is equally
        clear: these documents contain information that cannot be shared with a third party.
      </p>
      <p>
        This guide covers what the actual risks are, which approaches work, and how to use AI
        productively without sending sensitive data to anyone.
      </p>

      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mt-8 mb-3">What counts as sensitive</h2>
      <p>
        The categories that matter most in practice:
      </p>
      <ul className="space-y-2 pl-4">
        <li><strong className="text-gray-900 dark:text-white">Personal data.</strong> Names, addresses, national insurance numbers, dates of birth, email addresses. Regulated under GDPR in the UK and EU, with significant penalties for unlawful sharing.</li>
        <li><strong className="text-gray-900 dark:text-white">Legally privileged content.</strong> Communications between lawyers and clients, or documents prepared in anticipation of litigation. Sharing these with a third party, including an AI provider, can destroy the privilege.</li>
        <li><strong className="text-gray-900 dark:text-white">Confidential financial data.</strong> Client account numbers, portfolio values, transaction histories, forecasts. Subject to professional confidentiality obligations and often regulatory restrictions.</li>
        <li><strong className="text-gray-900 dark:text-white">Health information.</strong> Patient records, diagnoses, treatment plans. Heavily regulated in most jurisdictions.</li>
        <li><strong className="text-gray-900 dark:text-white">Commercially sensitive information.</strong> M&A targets, unreleased product plans, pricing strategy, counterparty negotiation positions.</li>
      </ul>

      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mt-8 mb-3">Why the risk is easy to underestimate</h2>
      <p>
        Chat interfaces feel private. There is no visible upload, no obvious transfer, no confirmation
        dialogue. The experience of typing into ChatGPT or Claude feels like thinking out loud, but
        it is not. Every character is transmitted to a server, stored, and processed by a company
        operating under its own terms of service.
      </p>
      <p>
        Sensitive data also hides in plain sight. A contract review request might contain three
        obviously sensitive terms (a client name, a company, and a deal value) buried across fifty
        paragraphs. Under time pressure, it is easy to paste the whole document and miss them.
      </p>

      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mt-8 mb-3">Approaches that work</h2>

      <h3 className="text-base font-semibold text-gray-800 dark:text-neutral-200 mt-6 mb-2">1. Redaction before sharing</h3>
      <p>
        Replace sensitive terms with neutral placeholders before you paste anything into an AI tool.
        Names become tokens, company names become codes, numbers become generics. The AI works with
        the structure and context of your document, which is usually what you need, and the sensitive
        values never leave your device.
      </p>
      <p>
        This is the most practical approach for most professionals because it works with any AI tool,
        requires no enterprise plan, and leaves a clear audit trail of what was and was not shared.
      </p>

      <h3 className="text-base font-semibold text-gray-800 dark:text-neutral-200 mt-6 mb-2">2. Enterprise AI plans with contractual protections</h3>
      <p>
        ChatGPT Enterprise, Claude for Enterprise, and similar offerings include contractual commitments
        that your data will not be used for training and may offer shorter retention windows. These are
        appropriate where your organisation already has data processing agreements in place and
        procurement controls. They do not eliminate the risk of data leaving your device; they manage
        what happens to it once it has.
      </p>

      <h3 className="text-base font-semibold text-gray-800 dark:text-neutral-200 mt-6 mb-2">3. On-premise or private AI deployment</h3>
      <p>
        Organisations with the technical capability can run AI models on their own infrastructure.
        This ensures data never leaves the organisation's environment. Practical for large firms with
        IT resources, but not realistic for most small practices or individuals.
      </p>

      <h3 className="text-base font-semibold text-gray-800 dark:text-neutral-200 mt-6 mb-2">4. Don't use AI for that document</h3>
      <p>
        Sometimes the right answer is that a particular document is too sensitive for any of the above
        approaches to be appropriate. Knowing when to draw that line is part of professional judgement.
      </p>

      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mt-8 mb-3">A practical redaction workflow</h2>
      <p>
        Here is a step-by-step approach that works for most document tasks:
      </p>
      <ol className="space-y-3 pl-4 list-decimal list-inside">
        <li>Paste or open your document in a redaction tool that runs locally in your browser.</li>
        <li>Identify and redact sensitive terms: names, company identifiers, account numbers, specific values, dates where relevant.</li>
        <li>Add a brief note in your AI prompt explaining that the text contains placeholder tokens (e.g., "xxxxx0001") that should be treated as opaque identifiers and preserved verbatim in the response.</li>
        <li>Copy the redacted text and paste it into your AI tool of choice.</li>
        <li>When you receive the AI's response, paste it back into the Restore tab of your redaction tool. The tokens are automatically replaced with the originals.</li>
      </ol>
      <p>
        The entire sensitive content of the document stays on your device. The AI works with a
        structurally identical version in which the sensitive values are replaced by meaningless tokens.
      </p>

      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mt-8 mb-3">What this does not solve</h2>
      <p>
        Redaction handles individual sensitive terms but is not a substitute for judgement about whether
        AI is appropriate at all for a given task. If the substance of a document, not just specific
        identifiers, is confidential, redacting names will not help. A contract with a novel and
        sensitive legal structure, for example, may be identifiable even without names attached.
      </p>
      <p>
        For the vast majority of practical use cases, however, including summarising a lease, reviewing
        standard clauses, extracting key dates, and drafting a response to a letter, redacting the
        identifiers is sufficient to allow safe, productive use of AI tools.
      </p>
    </BlogLayout>
  )
}
